import { useEffect, useState } from "react";
import { api, formatIDR, formatNumber } from "../lib/api";
import { FlaskConical, Radiation, Stethoscope, Pill, Building2, Package, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

function StatCard({ label, value, icon: Icon, testid }) {
    return (
        <div className="surface-card p-6" data-testid={testid}>
            <div className="flex items-start justify-between mb-4">
                <span className="label-uppercase">{label}</span>
                <Icon size={20} className="text-[#6A9C89]" strokeWidth={1.5} />
            </div>
            <div className="font-heading text-3xl font-semibold text-[#1A362D]">{value}</div>
        </div>
    );
}

export default function Dashboard() {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/dashboard/stats").then((res) => setStats(res.data));
    }, []);

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <div>
                <div className="label-uppercase mb-2">Ringkasan</div>
                <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-semibold text-[#1A362D]">
                    Dashboard
                </h1>
                <p className="text-[#4A5E57] mt-2 max-w-2xl">
                    Ringkasan cepat data master pemeriksaan, unit cost terkini per pemeriksaan,
                    dan paket MCU yang telah tersimpan.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    label="Pemeriksaan Lab"
                    value={stats ? formatNumber(stats.lab_count) : "…"}
                    icon={FlaskConical}
                    testid="stat-lab-count"
                />
                <StatCard
                    label="Pemeriksaan Radiologi"
                    value={stats ? formatNumber(stats.radiology_count) : "…"}
                    icon={Radiation}
                    testid="stat-rad-count"
                />
                <StatCard
                    label="Jasa Tindakan"
                    value={stats ? formatNumber(stats.jasa_count) : "…"}
                    icon={Stethoscope}
                    testid="stat-jasa-count"
                />
                <StatCard
                    label="Obat & BMHP"
                    value={stats ? formatNumber(stats.obat_count) : "…"}
                    icon={Pill}
                    testid="stat-obat-count"
                />
                <StatCard
                    label="Fasilitas"
                    value={stats ? formatNumber(stats.fasilitas_count) : "…"}
                    icon={Building2}
                    testid="stat-fasilitas-count"
                />
                <StatCard
                    label="Unit Cost Lab (per pemeriksaan)"
                    value={stats ? formatIDR(stats.unit_cost_lab) : "…"}
                    icon={TrendingUp}
                    testid="stat-uc-lab"
                />
                <StatCard
                    label="Unit Cost Radiologi"
                    value={stats ? formatIDR(stats.unit_cost_radiology) : "…"}
                    icon={TrendingUp}
                    testid="stat-uc-rad"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 surface-card p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="label-uppercase">Paket MCU Terbaru</div>
                            <h3 className="font-heading text-xl font-medium mt-1">Riwayat Paket</h3>
                        </div>
                        <Link
                            to="/mcu"
                            data-testid="new-package-link"
                            className="text-sm font-medium text-[#245B4B] hover:text-[#1A4236]"
                        >
                            + Buat Paket Baru
                        </Link>
                    </div>
                    {stats?.latest_packages?.length ? (
                        <div className="divide-y divide-[#D1DCD8]">
                            {stats.latest_packages.map((p) => (
                                <div
                                    key={p.id}
                                    className="py-3 flex items-center justify-between"
                                    data-testid={`recent-pkg-${p.id}`}
                                >
                                    <div>
                                        <div className="font-medium">{p.name}</div>
                                        <div className="text-xs text-[#6A9C89]">
                                            {p.items?.length || 0} pemeriksaan
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-heading font-semibold">
                                            {formatIDR(p.final_price)}
                                        </div>
                                        <div className="text-xs text-[#6A9C89]">Harga jual</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-sm text-[#6A9C89] py-8 text-center">
                            Belum ada paket MCU tersimpan.
                        </div>
                    )}
                </div>

                <div className="surface-card p-6" data-testid="stat-pkg-count">
                    <div className="flex items-center gap-3 mb-4">
                        <Package className="text-[#245B4B]" size={22} />
                        <span className="label-uppercase">Paket Tersimpan</span>
                    </div>
                    <div className="font-heading text-5xl font-semibold text-[#1A362D]">
                        {stats ? formatNumber(stats.package_count) : "…"}
                    </div>
                    <p className="text-sm text-[#4A5E57] mt-4 leading-relaxed">
                        Paket MCU yang tersimpan dapat digenerate ulang menjadi PDF kop surat
                        dan dibagikan via WhatsApp kapan saja.
                    </p>
                    <Link
                        to="/packages"
                        className="mt-6 inline-block text-sm font-medium text-[#245B4B] hover:text-[#1A4236]"
                    >
                        Lihat semua paket →
                    </Link>
                </div>
            </div>
        </div>
    );
}
